import React from "react";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";
import contractLawDetailsStyles from "./contractLawDetailsStyles";
import ContractSpecificDetailsSection from "./ContractSpecificDetailsSection";
import _includes from "lodash/includes";
import Typography from "@mui/material/Typography";
import CaseDetailsItem from "../../Common/CaseDetailsItem/CaseDetailsItem";
import { AbstractCase } from "../../../../types/Entities/AbstractCase";

type ExpandedGeneralContractLawCaseDetailsProps = {
  contractLawCase: AbstractCase;
};

const ExpandedGeneralContractLawCaseDetails = ({ contractLawCase }: ExpandedGeneralContractLawCaseDetailsProps) => {
  return (
    <>
      {contractLawCase.caseCategory === "enforceClaim" && (
        <Grid container>
          <CaseDetailsItem caseData={contractLawCase} fieldPath={"objectiveClient"} />
          <CaseDetailsItem caseData={contractLawCase} fieldPath={"clientHasAlreadyClaimed"} />

          {contractLawCase.clientHasAlreadyClaimed && <CaseDetailsItem caseData={contractLawCase} fieldPath={"claimedDateClient"} fieldType={"date"} />}
          <CaseDetailsItem caseData={contractLawCase} fieldPath={"subjectOfDispute"} />
          {contractLawCase.subjectOfDispute === "money" && (
            <>
              <CaseDetailsItem caseData={contractLawCase} fieldPath={"claimedAmountClient"} fieldType={"money"} />
              <CaseDetailsItem caseData={contractLawCase} fieldPath={"client.bankData.iban"} />
            </>
          )}
        </Grid>
      )}
      {contractLawCase.caseCategory === "defendClaim" && (
        <>
          <Grid container>
            <CaseDetailsItem caseData={contractLawCase} fieldPath={"defenseProcessStatus"} />
            {contractLawCase.defenseProcessStatus === "paymentOrder" && (
              <CaseDetailsItem caseData={contractLawCase} fieldPath={"paymentOrderDate"} fieldType={"date"} />
            )}
            {contractLawCase.defenseProcessStatus === "enforcementOrder" && (
              <CaseDetailsItem caseData={contractLawCase} fieldPath={"enforcementOrderDate"} fieldType={"date"} />
            )}
            {contractLawCase.defenseProcessStatus === "lawsuit" && (
              <CaseDetailsItem caseData={contractLawCase} fieldPath={"statementOfClaimFiledDate"} fieldType={"date"} />
            )}
            {_includes(["paymentOrder", "enforcementOrder", "reminder", "lawsuit"], contractLawCase.defenseProcessStatus) && (
              <CaseDetailsItem caseData={contractLawCase} fieldPath={"contractualPartnerHasLawyer"} />
            )}
            {contractLawCase.contractualPartnerHasLawyer && (
              <>
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opponentLawyer.gender"} labelPostfix={"Anwalt"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opponentLawyer.givenName"} labelPostfix={"Anwalt"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opponentLawyer.familyName"} labelPostfix={"Anwalt"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opponentLawFirm.organization.name"} labelPostfix={"Kanzlei"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opponentLawFirm.organization.address.streetAddress"} labelPostfix={"Kanzlei"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opponentLawFirm.organization.address.postalCode"} labelPostfix={"Kanzlei"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opponentLawFirm.organization.address.addressLocality"} labelPostfix={"Kanzlei"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opponentLawFirm.organization.mainEmail.email"} labelPostfix={"Kanzlei"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opponentLawFirm.organization.telephone"} labelPostfix={"Kanzlei"} />
              </>
            )}
            <CaseDetailsItem caseData={contractLawCase} fieldPath={"subjectOfDispute"} />
            {contractLawCase.subjectOfDispute === "money" && (
              <CaseDetailsItem caseData={contractLawCase} fieldPath={"claimedAmountClient"} fieldType={"money"} />
            )}
          </Grid>
        </>
      )}

      <ContractSpecificDetailsSection contractLawCase={contractLawCase} />

      {contractLawCase.caseCategory !== "advisory" && (
        <>
          <Typography variant={"h5"} sx={contractLawDetailsStyles.subheader}>
            Angaben zum Vertragspartner
          </Typography>
          <Box sx={contractLawDetailsStyles.divider} />
          <Grid container>
            {contractLawCase.opposingPartyType === "opposingPartyLegalEntity" && (
              <>
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opposingPartyLegalEntity.organization.legalName"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opposingPartyLegalEntity.organization.address.streetAddress"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opposingPartyLegalEntity.organization.address.postalCode"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opposingPartyLegalEntity.organization.address.addressLocality"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opposingPartyLegalEntity.organization.address.postOfficeBoxNumber"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opposingPartyLegalEntity.organization.address.postOfficeBoxPostalCode"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opposingPartyLegalEntity.organization.address.postOfficeBoxAddressLocality"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opposingPartyLegalEntity.organization.mainEmail.email"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opposingPartyLegalEntity.organization.telephone"} />
              </>
            )}
            {contractLawCase.opposingPartyType === "opposingPartyPerson" && (
              <>
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opposingPartyPerson.gender"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opposingPartyPerson.givenName"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opposingPartyPerson.familyName"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opposingPartyPerson.residenceAddress.streetAddress"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opposingPartyPerson.residenceAddress.postalCode"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opposingPartyPerson.residenceAddress.addressLocality"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opposingPartyPerson.email"} />
                <CaseDetailsItem caseData={contractLawCase} fieldPath={"opposingPartyPerson.telephone"} />
              </>
            )}
          </Grid>
        </>
      )}
      {contractLawCase.caseCategory === "defendClaim" && contractLawCase.defenseProcessStatus === "debtCollection" && (
        <>
          <Typography variant={"h6"} sx={contractLawDetailsStyles.subheader}>
            Angaben zum Inkassobüro
          </Typography>
          <Box sx={contractLawDetailsStyles.divider} />
          <Grid container>
            <CaseDetailsItem caseData={contractLawCase} fieldPath={"debtCollectionAgency.organization.name"} />
            <CaseDetailsItem caseData={contractLawCase} fieldPath={"debtCollectionAgencyReference"} />
            <CaseDetailsItem caseData={contractLawCase} fieldPath={"debtCollectionAgency.organization.address.streetAddress"} />
            <CaseDetailsItem caseData={contractLawCase} fieldPath={"debtCollectionAgency.organization.address.postalCode"} />
            <CaseDetailsItem caseData={contractLawCase} fieldPath={"debtCollectionAgency.organization.address.addressLocality"} />
            <CaseDetailsItem caseData={contractLawCase} fieldPath={"debtCollectionAgency.organization.mainEmail.email"} />
            <CaseDetailsItem caseData={contractLawCase} fieldPath={"debtCollectionAgency.organization.telephone"} />
          </Grid>
        </>
      )}
      <Typography variant={"h6"} sx={contractLawDetailsStyles.subheader}>
        Beschreibung der Situation
      </Typography>
      <Box sx={contractLawDetailsStyles.divider} />
      <Grid container>
        <CaseDetailsItem caseData={contractLawCase} fieldPath={"personalTextMessage"} />
      </Grid>
      <Typography variant={"h6"} sx={contractLawDetailsStyles.subheader}>
        Versicherung
      </Typography>
      <Box sx={contractLawDetailsStyles.divider} />
      <Grid container>
        <CaseDetailsItem caseData={contractLawCase} fieldPath={"caseInsurance.insurer.name"} />
        <CaseDetailsItem caseData={contractLawCase} fieldPath={"caseInsurance.insurancePolicyNumber"} />
        <CaseDetailsItem caseData={contractLawCase} fieldPath={"caseInsurance.caseReferenceNumber"} />
        <CaseDetailsItem caseData={contractLawCase} fieldPath={"caseInsurance.policyHolderType"} />

        {contractLawCase.caseInsurance.policyHolderType === "familyMember" && (
          <>
            <CaseDetailsItem caseData={contractLawCase} fieldPath={"caseInsurance.policyHolder.gender"} labelPostfix={"Versicherungsnehmer"} />
            <CaseDetailsItem caseData={contractLawCase} fieldPath={"caseInsurance.policyHolder.givenName"} labelPostfix={"Versicherungsnehmer"} />
            <CaseDetailsItem caseData={contractLawCase} fieldPath={"caseInsurance.policyHolder.familyName"} labelPostfix={"Versicherungsnehmer"} />
          </>
        )}
      </Grid>
    </>
  );
};

export default ExpandedGeneralContractLawCaseDetails;
